import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';

const HashtagGeneratorPage = () => {
  const tool = getToolById('hashtag-generator');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [niche, setNiche] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const platforms = [
    { id: 'instagram', name: 'Instagram', limit: 30 },
    { id: 'twitter', name: 'Twitter/X', limit: 10 },
    { id: 'linkedin', name: 'LinkedIn', limit: 15 },
    { id: 'tiktok', name: 'TikTok', limit: 20 }
  ];

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      const selectedPlatform = platforms.find(p => p.id === platform);
      let hashtags = [];
      
      // Core topic hashtags
      const topicWords = topic.toLowerCase().split(' ');
      hashtags.push(`#${topicWords.join('')}`);
      topicWords.forEach(word => {
        if (word.length > 3) hashtags.push(`#${word}`);
      });
      
      // Platform-specific popular hashtags
      if (platform === 'instagram') {
        hashtags.push(
          '#instagood', '#photooftheday', '#love', '#instadaily', '#follow',
          '#picoftheday', '#instamood', '#instalike', '#igers', '#instacool',
          '#amazing', '#smile', '#style', '#swag', '#family', '#cool',
          '#nofilter', '#happy', '#sun', '#sky', '#nature', '#night',
          '#morning', '#beauty', '#light', '#portrait', '#colors'
        );
      } else if (platform === 'twitter') {
        hashtags.push(
          '#MondayMotivation', '#TuesdayTips', '#WednesdayWisdom', 
          '#ThrowbackThursday', '#FollowFriday', '#SaturdayVibes', '#SundayFunday'
        );
      } else if (platform === 'linkedin') {
        hashtags.push(
          '#business', '#entrepreneur', '#leadership', '#marketing', '#sales',
          '#networking', '#career', '#success', '#innovation', '#strategy',
          '#growth', '#productivity', '#professional', '#industry', '#insights'
        );
      } else if (platform === 'tiktok') {
        hashtags.push(
          '#fyp', '#foryou', '#viral', '#trending', '#duet', '#challenge',
          '#funny', '#comedy', '#dance', '#music', '#tutorial', '#tips',
          '#hack', '#diy', '#lifestyle', '#fashion', '#beauty', '#food'
        );
      }
      
      // Niche-specific hashtags
      if (niche) {
        const nicheWords = niche.toLowerCase().split(' ');
        hashtags.push(`#${nicheWords.join('')}`);
        nicheWords.forEach(word => {
          if (word.length > 3) hashtags.push(`#${word}`);
        });
        
        // Add related niche hashtags based on common niches
        if (niche.toLowerCase().includes('fitness') || niche.toLowerCase().includes('health')) {
          hashtags.push('#fitness', '#health', '#workout', '#gym', '#motivation', '#fit', '#healthy', '#exercise', '#wellness', '#strong');
        } else if (niche.toLowerCase().includes('food') || niche.toLowerCase().includes('recipe')) {
          hashtags.push('#food', '#foodie', '#recipe', '#cooking', '#delicious', '#yummy', '#chef', '#kitchen', '#meal', '#tasty');
        } else if (niche.toLowerCase().includes('travel')) {
          hashtags.push('#travel', '#wanderlust', '#adventure', '#explore', '#vacation', '#trip', '#tourism', '#traveler', '#journey', '#destination');
        } else if (niche.toLowerCase().includes('business') || niche.toLowerCase().includes('entrepreneur')) {
          hashtags.push('#business', '#entrepreneur', '#startup', '#success', '#money', '#investment', '#finance', '#wealth', '#mindset', '#hustle');
        }
      }
      
      // Remove duplicates and limit based on platform
      const uniqueHashtags = [...new Set(hashtags)];
      const limitedHashtags = uniqueHashtags.slice(0, selectedPlatform.limit);
      
      // Organize hashtags by popularity/size
      const popularHashtags = limitedHashtags.slice(0, Math.floor(limitedHashtags.length * 0.3));
      const mediumHashtags = limitedHashtags.slice(Math.floor(limitedHashtags.length * 0.3), Math.floor(limitedHashtags.length * 0.7));
      const nicheHashtags = limitedHashtags.slice(Math.floor(limitedHashtags.length * 0.7));
      
      const generatedContent = `🎯 HASHTAG STRATEGY FOR ${selectedPlatform.name.toUpperCase()}

📈 POPULAR HASHTAGS (High Competition, High Reach):
${popularHashtags.join(' ')}

🎪 MEDIUM HASHTAGS (Moderate Competition, Good Reach):
${mediumHashtags.join(' ')}

🔍 NICHE HASHTAGS (Low Competition, Targeted Reach):
${nicheHashtags.join(' ')}

📋 COPY-READY FORMAT:
${limitedHashtags.join(' ')}

💡 PRO TIPS FOR ${selectedPlatform.name}:
${platform === 'instagram' ? `
• Use all 30 hashtags for maximum reach
• Mix popular, medium, and niche hashtags (30/30/40 rule)
• Put hashtags in the first comment to keep caption clean
• Create branded hashtags for your community
• Research hashtags before using them` : ''}
${platform === 'twitter' ? `
• Limit to 1-2 hashtags per tweet for better engagement
• Join trending conversations with relevant hashtags
• Use hashtags at the end of your tweet
• Create event-specific hashtags
• Monitor hashtag performance regularly` : ''}
${platform === 'linkedin' ? `
• Use 3-5 relevant hashtags maximum
• Focus on industry and professional hashtags
• Place hashtags at the end of your post
• Follow hashtags in your niche
• Create thought leadership with consistent hashtag use` : ''}
${platform === 'tiktok' ? `
• Use trending hashtags for discovery
• Mix viral hashtags with niche ones
• Check hashtag view counts before using
• Jump on trending challenges quickly
• Create unique hashtags for your content series` : ''}

🎯 HASHTAG SIZES:
• Popular (1M+ posts): High competition, broad reach
• Medium (100K-1M posts): Moderate competition, good engagement  
• Niche (10K-100K posts): Low competition, targeted audience`;
      
      setResult(generatedContent);
      setIsGenerating(false);
    }, 1800);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Content Topic *
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., morning workout, healthy recipes, business tips"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Platform
                  </label>
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  >
                    {platforms.map(p => (
                      <option key={p.id} value={p.id}>{p.name} (Max {p.limit} hashtags)</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Your Niche (Optional)
                  </label>
                  <input
                    type="text"
                    value={niche}
                    onChange={(e) => setNiche(e.target.value)}
                    placeholder="e.g., fitness, food blogging, digital marketing, travel"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HashtagGeneratorPage;